import { useWorkspaceText } from './interface-copy';
import { useEffect, useState, type ReactNode } from 'react';
import { useCompany } from '@/company/context';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import inboxCopy, { type InboxCopy } from '@/i18n/ns/inbox';
import { useInboxKindLabels, useInboxRowMeta } from './inbox-extensions';
import './apps/inbox-kinds';
import './agent-requests/inbox-kinds';
import { useWorkspace } from './context';
import workspaceTopbarCopy from '@/i18n/ns/workspace-topbar';
import { WorkspaceIcon, type WorkspaceIconName } from './icons';
import { EmptyState, ErrorState, LoadingState, PageHeading } from './primitives';
import { sharingApi, type SharingRequest } from './sharing-api';
import { inboxApi, useInbox, type Inbox, type InboxItem, type InboxUpdate } from './inbox-api';
import {
  groupUpdates,
  inboxCounts,
  kindCopyKey,
  kindIcon,
  itemTitle,
  rowIdFor,
  sharingItemId,
  updateExcerpt,
  whenLabel,
  type UpdateGroup,
} from './inbox-model';
import { useIntlLocale } from './intl-locale';
import './inbox.css';

// The Inbox (collaboration v1, D57 decision 8; POLISH PL-15). A flat, scannable queue
// in the page frame. Every item opens in the one canvas (CANVAS-NAVIGATION), where it
// is read and, when it asks for one, decided (DESIGN-SPEC §7). "Needs you" is derived
// live on the server; "Updates" are personal and grant nothing.

export const kindLabel = (
  copy: InboxCopy,
  kind: string,
  extended?: (kind: string) => string | null,
) => extended?.(kind) ?? copy[kindCopyKey(kind)];
/** The kind a sharing request reads as, when the Inbox lists do not hold it. */
export const requestKind = (request: Pick<SharingRequest, 'action'>) =>
  request.action === 'publish_skill' ? 'skill_publish_review' : 'sharing_review';

export function useInboxKey() {
  const ws = useWorkspace();
  const company = useCompany();
  const companyId = company.data?.company?.id;
  return ws.user && companyId ? `${ws.user.id}\u0000${companyId}` : null;
}

/** The sidebar entry: a count of what needs you, or a quiet dot for unread updates. */
export function InboxNavItem() {
  const ws = useWorkspace();
  const copy = useNamespaceCopy(inboxCopy);
  const topbar = useNamespaceCopy(workspaceTopbarCopy);
  const { inbox } = useInbox(useInboxKey());
  const { needsYou, unreadRows } = inboxCounts(inbox);
  const active = ws.view === 'inbox';
  return (
    <button
      className={`ws-nav-item ws-inbox-nav ${active ? 'active' : ''}`}
      aria-current={active ? 'page' : undefined}
      title={topbar.inbox}
      onClick={() => ws.navigate('inbox')}
    >
      <WorkspaceIcon name="inbox" />
      <span>{topbar.inbox}</span>
      {needsYou > 0 ? (
        <small
          className="ws-inbox-count"
          aria-label={formatCopy(copy.badgeNeeds, { count: needsYou })}
        >
          {needsYou}
        </small>
      ) : (
        unreadRows > 0 && (
          <small className="ws-inbox-news" role="img" aria-label={copy.newUpdates} />
        )
      )}
    </button>
  );
}

function Row({
  icon,
  title,
  meta,
  when,
  at,
  unread,
  selected,
  onOpen,
  copy,
}: {
  icon: WorkspaceIconName;
  title: string;
  meta: string;
  when: string;
  at: string | null | undefined;
  unread: boolean;
  selected: boolean;
  onOpen: () => void;
  copy: InboxCopy;
}) {
  return (
    <li>
      <button
        type="button"
        className="ws-inbox-row"
        aria-current={selected ? 'true' : undefined}
        data-unread={unread || undefined}
        onClick={onOpen}
      >
        <span className="ws-inbox-dot">
          {unread && <span className="ws-sr-only">{copy.unread}</span>}
        </span>
        <WorkspaceIcon name={icon} size={16} className="ws-inbox-kind" />
        <span className="ws-inbox-row-text">
          <strong>{title}</strong>
          {meta && <small>{meta}</small>}
        </span>
        {when && (
          <time className="ws-inbox-when" dateTime={at ?? undefined}>
            {when}
          </time>
        )}
      </button>
    </li>
  );
}

function Section({
  title,
  count,
  tools,
  children,
}: {
  title: string;
  count?: number;
  tools?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="ws-inbox-section" aria-label={title}>
      <h2 className="ws-inbox-group">
        <span>{title}</span>
        {!!count && <span className="ws-inbox-count">{count}</span>}
        {tools}
      </h2>
      <ul className="ws-inbox-rows">{children}</ul>
    </section>
  );
}

const joined = (...parts: (string | null | undefined | false)[]) =>
  parts.filter(Boolean).join(' · ');

// Decided sharing requests (the bounded list the Library used to show: pending and recent
// decisions, up to 100), read when the page opens so the section exists only when it has
// something in it. Closed until asked for.
function RecentDecisions({
  selectedId,
  onOpen,
  onLoaded,
  copy,
  locale,
}: {
  selectedId: string | null;
  onOpen: (requestId: string) => void;
  onLoaded: (count: number) => void;
  copy: InboxCopy;
  locale: string;
}) {
  const [requests, setRequests] = useState<SharingRequest[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);
  async function load() {
    setFailed(false);
    try {
      const value = await sharingApi.list();
      const decided = value.requests.filter((request) => request.status !== 'pending');
      setRequests(decided);
      onLoaded(decided.length);
    } catch {
      setFailed(true);
    }
  }
  useEffect(() => {
    void load();
  }, []);
  if (!failed && !requests?.length) return null;
  return (
    <section className="ws-inbox-section ws-inbox-history" aria-label={copy.decisions}>
      <h2 className="ws-inbox-group">
        <button
          type="button"
          className="ws-inbox-disclosure"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          <WorkspaceIcon name="chevron" size={14} />
          <span>{copy.decisions}</span>
          {!!requests?.length && <span className="ws-inbox-count quiet">{requests.length}</span>}
        </button>
      </h2>
      {open &&
        (failed ? (
          <ErrorState message={copy.decisionsFailed} onRetry={() => void load()} />
        ) : (
          <ul className="ws-inbox-rows">
            {requests!.map((request) => (
              <Row
                key={request.id}
                icon={kindIcon(requestKind(request))}
                title={request.title || copy.untitled}
                meta={joined(
                  request.status === 'approved' ? copy.approved : copy.declined,
                  kindLabel(copy, requestKind(request)),
                )}
                when={whenLabel(request.decidedAt ?? request.createdAt, locale)}
                at={request.decidedAt ?? request.createdAt}
                unread={false}
                selected={selectedId === sharingItemId(request.id)}
                onOpen={() => onOpen(request.id)}
                copy={copy}
              />
            ))}
          </ul>
        ))}
    </section>
  );
}

// The row a canvas shows: an Inbox canvas by its id (an earlier run selects its group), a
// page suggestion by the item that references it, before and after its link resolves to
// the page with that suggestion in focus.
function selectedRow(
  inbox: Inbox,
  canvas: { kind: string; id?: string; suggestion?: string } | null,
) {
  if (canvas?.kind === 'inbox') return rowIdFor(inbox, canvas.id);
  const suggestion =
    canvas?.kind === 'page-proposal'
      ? canvas.id
      : canvas?.kind === 'page'
        ? canvas.suggestion
        : undefined;
  if (suggestion)
    return (
      inbox.needsYou.find(
        (item) => item.ref?.type === 'page_proposal' && item.ref.id === suggestion,
      )?.id ?? null
    );
  return null;
}

export function InboxPage() {
  const { text: uiText } = useWorkspaceText();
  const owner = useInboxKey();
  return <InboxPageContent key={owner} />;
}

function InboxPageContent() {
  const { text: uiText } = useWorkspaceText();
  const ws = useWorkspace();
  const copy = useNamespaceCopy(inboxCopy);
  const topbar = useNamespaceCopy(workspaceTopbarCopy);
  const extended = useInboxKindLabels();
  const rowMeta = useInboxRowMeta();
  const locale = useIntlLocale();
  const { inbox, error, refresh } = useInbox(useInboxKey());
  const [decisions, setDecisions] = useState(0);
  useEffect(() => {
    void refresh();
  }, []);
  if (!inbox)
    return error ? (
      <ErrorState message={error} onRetry={() => void refresh()} />
    ) : (
      <LoadingState label={copy.loading} />
    );
  const selected = selectedRow(inbox, ws.canvas);
  // A knowledge-page suggestion already has its own review canvas; everything else
  // opens the Inbox canvas for that item.
  const openItem = (item: InboxItem) =>
    ws.openCanvas(
      item.ref?.type === 'page_proposal'
        ? { kind: 'page-proposal', id: item.ref.id }
        : { kind: 'inbox', id: item.id },
    );
  const openUpdate = (update: InboxUpdate) => ws.openCanvas({ kind: 'inbox', id: update.id });
  const groups = groupUpdates(inbox.updates);
  const { needsYou } = inboxCounts(inbox);
  const unread = groups.some((group) => group.unread.length);
  const empty =
    !inbox.needsYou.length && !inbox.updates.length && !inbox.waiting.length && !decisions;
  async function markAllRead() {
    try {
      await inboxApi.markRead({ all: true });
      await refresh();
    } catch {
      ws.notify(copy.readFailed, null, 'error');
    }
  }
  const itemRow = (item: InboxItem) => (
    <Row
      key={item.id}
      icon={kindIcon(item.kind)}
      title={rowMeta(item).title ?? itemTitle(item)}
      meta={joined(
        kindLabel(copy, item.kind, extended),
        rowMeta(item).by ?? item.actor?.name,
        rowMeta(item).detail,
      )}
      when={whenLabel(item.createdAt, locale)}
      at={item.createdAt}
      unread={false}
      selected={selected === item.id}
      onOpen={() => openItem(item)}
      copy={copy}
    />
  );
  const updateRow = (group: UpdateGroup) => (
    <Row
      key={group.latest.id}
      icon={kindIcon(group.latest.kind)}
      title={rowMeta(group.latest).title ?? group.latest.title}
      meta={joined(
        kindLabel(copy, group.latest.kind, extended),
        rowMeta(group.latest).by ?? group.latest.actor?.name,
        group.items.length > 1 && formatCopy(copy.latestOf, { count: group.items.length }),
        rowMeta(group.latest).detail ?? updateExcerpt(group.latest),
      )}
      when={whenLabel(group.latest.createdAt, locale)}
      at={group.latest.createdAt}
      unread={group.unread.length > 0}
      selected={selected === group.latest.id}
      onOpen={() => openUpdate(group.latest)}
      copy={copy}
    />
  );
  return (
    <div className="ws-inbox">
      <PageHeading
        eyebrow={uiText('{company} workspace', { company: ws.companyName })}
        title={topbar.inbox}
        // An empty Inbox says so once, in the empty state below, not also in the subtitle.
        description={needsYou ? copy.needsSome : empty ? undefined : copy.needsNone}
      />
      {empty && <EmptyState icon="inbox" title={copy.emptyTitle} description={copy.emptyBody} />}
      {inbox.needsYou.length > 0 && (
        <Section title={copy.needsYou} count={needsYou}>
          {inbox.needsYou.map(itemRow)}
        </Section>
      )}
      {groups.length > 0 && (
        <Section
          title={copy.updates}
          tools={
            unread && (
              <button type="button" className="ws-inbox-mark" onClick={() => void markAllRead()}>
                {copy.markAllRead}
              </button>
            )
          }
        >
          {groups.map(updateRow)}
        </Section>
      )}
      {inbox.waiting.length > 0 && (
        <Section title={copy.waiting}>{inbox.waiting.map(itemRow)}</Section>
      )}
      <RecentDecisions
        selectedId={ws.canvas?.kind === 'inbox' ? (ws.canvas.id ?? null) : null}
        onOpen={(requestId) => ws.openCanvas({ kind: 'inbox', id: sharingItemId(requestId) })}
        onLoaded={setDecisions}
        copy={copy}
        locale={locale}
      />
    </div>
  );
}
